import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Lightning, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { roleLabel } from "../lib/labels";

function DoneState() {
  return (
    <>
      <CheckCircle size={48} weight="fill" className="text-[#22C55E] mx-auto mb-4" />
      <h1 className="font-heading text-2xl font-bold mb-2">You're in</h1>
      <p className="text-[color:var(--text-secondary)]">Redirecting to your project...</p>
    </>
  );
}

function ErrorState({ message }) {
  return (
    <>
      <h1 className="font-heading text-2xl font-bold mb-2 text-red-400">Invite invalid</h1>
      <p className="text-[color:var(--text-secondary)] mb-6">{message}</p>
      <Link to="/dashboard" className="btn-ghost">Back to dashboard</Link>
    </>
  );
}

function InviteState({ info, onAccept, accepting, error }) {
  const friendlyRole = roleLabel(info.invite.role);
  return (
    <>
      <Lightning size={32} weight="fill" className="text-[#007AFF] mx-auto mb-4" />
      <p className="label-mono mb-2">Project invitation</p>
      <h1 className="font-heading text-2xl font-bold mb-2">{info.project_name}</h1>
      <p className="text-[color:var(--text-secondary)] mb-2">
        You'll join as <span className={`role-badge role-${info.invite.role}`}>{friendlyRole}</span>
      </p>
      <p className="text-xs text-[color:var(--text-tertiary)] mb-6">
        Your global role will be updated to <strong>{friendlyRole}</strong>.
      </p>
      {error && (
        <div data-testid="accept-invite-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded px-3 py-2 mb-4 text-left">
          {error}
        </div>
      )}
      <button data-testid="accept-invite-button" onClick={onAccept} disabled={accepting} className="btn-primary w-full justify-center">
        {accepting ? "Joining..." : "Accept invite"}
      </button>
    </>
  );
}

function renderInviteBody({ done, error, info, accept, accepting }) {
  if (done) return <DoneState />;
  // If we already have invite info, keep showing it and render the error inline
  // (nicer UX than replacing the whole card with a bare error message).
  if (info) return <InviteState info={info} onAccept={accept} accepting={accepting} error={error} />;
  if (error) return <ErrorState message={error} />;
  return <p className="text-[color:var(--text-tertiary)]">Loading invite...</p>;
}

export default function AcceptInvite() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}`);
        setInfo(data);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail));
      }
    })();
  }, [token]);

  const accept = async () => {
    setError("");
    setAccepting(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`);
      setDone(true);
      setTimeout(() => navigate(`/projects/${data.project_id}`), 1200);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
    setAccepting(false);
  };

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center text-[color:var(--text-tertiary)]">Loading...</div>;
  }

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="surface p-10 rounded-md max-w-md w-full text-center">
          <Lightning size={32} weight="fill" className="text-[#007AFF] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">You've been invited</h1>
          <p className="text-[color:var(--text-secondary)] mb-6">Sign in or create an account to accept this invite.</p>
          <Link to={`/login`} state={{ from: `/invite/${token}` }} className="btn-primary w-full justify-center" data-testid="invite-signin-link">
            Sign in to accept <ArrowRight size={16} />
          </Link>
          <Link to={`/register`} className="btn-ghost w-full justify-center mt-3" data-testid="invite-signup-link">
            Create new account
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="surface p-10 rounded-md max-w-md w-full text-center" data-testid="accept-invite-card">
        {renderInviteBody({ done, error, info, accept, accepting })}
      </div>
    </div>
  );
}
